import EditNoteIcon from "@mui/icons-material/EditNote";
import HistoryIcon from "@mui/icons-material/History";
import UpdateIcon from "@mui/icons-material/Update";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router";
import WorkAgreement from "work/work-agreements/work-agreement";

type Props = Readonly<{
  workAgreement: WorkAgreement;
}>;

/**
 * The two ends of a revision, each pointing at the other.
 *
 * Hiring a revision ends the agreement it revised and starts a new one in its place, and the two
 * carry a link in both directions: the ended agreement names the revision that replaced it, and
 * the revision names the agreement it revised. Anyone reading either can get to the other.
 *
 * The routes are the archived and active views respectively, which is where each normally lives
 * -- a replaced agreement has ended, and the agreement that replaced it is the live one. Either
 * page corrects its own route from the agreement's archive state, so a revision that has itself
 * since ended still opens.
 *
 * A third link points the other way in time: while a revision is still being negotiated, the
 * agreement it was opened from names it, so the leaders reading live work can reach the change being
 * asked of it without hunting for it in the proposals list. The server decides who is told -- an
 * unsubmitted draft is private to the party that opened it -- so the id being here at all is the
 * permission to show it.
 *
 * Renders nothing for an agreement that is none of those: a first-time agreement with nothing in
 * flight against it has nothing to point at.
 */
export default function WorkAgreementRevisionLinks({ workAgreement }: Props) {
  const { openRevisionProposalId, replacedByWorkAgreementId, replacesWorkAgreementId } = workAgreement;
  if (!openRevisionProposalId && !replacedByWorkAgreementId && !replacesWorkAgreementId) return null;

  return (
    <Stack spacing={1.5}>
      {openRevisionProposalId && (
        <Stack spacing={0.5}>
          <Button
            component={Link}
            size="small"
            startIcon={<EditNoteIcon />}
            sx={{ alignSelf: "flex-start" }}
            to={`/proposals/active/revise/${openRevisionProposalId.value}/details`}
          >
            Open the revision request
          </Button>
          <Typography color="text.secondary" variant="body2">
            A revision of this agreement is being negotiated. This agreement runs unchanged until that
            revision is hired.
          </Typography>
        </Stack>
      )}
      {replacedByWorkAgreementId && (
        <Stack spacing={0.5}>
          <Button
            component={Link}
            size="small"
            startIcon={<UpdateIcon />}
            sx={{ alignSelf: "flex-start" }}
            to={`/work/active/view/${replacedByWorkAgreementId.value}/details`}
          >
            Open the current revision of this work
          </Button>
          <Typography color="text.secondary" variant="body2">
            This agreement ended when a revision of it was hired. The current revision replaced it.
          </Typography>
        </Stack>
      )}
      {replacesWorkAgreementId && (
        <Stack spacing={0.5}>
          <Button
            component={Link}
            size="small"
            startIcon={<HistoryIcon />}
            sx={{ alignSelf: "flex-start" }}
            to={`/work/archived/view/${replacesWorkAgreementId.value}/details`}
          >
            Open the previous revision of this work
          </Button>
          <Typography color="text.secondary" variant="body2">
            This agreement was negotiated as a revision of the previous one, which ended when this was hired.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
